<template>
  <v-card
    v-if="!isExpiredAgreement"
    class="b-1"
    data-testid="dashboard-task"
    elevation="0"
    style="border-color: #f79009"
    rounded
  >
    <v-card-text>
      <v-row>
        <v-col class="d-flex align-center">
          <v-avatar
            class="me-3"
            color="#F79009"
            size="40"
            variant="tonal"
          >
            <v-icon
              color="#F79009"
              icon="priority_high"
              size="24"
            />
          </v-avatar>

          <template v-if="task.wait_list_offer">
            <h3 class="fs-18">
              {{ task.child_first_name }}
              {{ task.child_last_name }}
              <span class="fw-500">
                {{ $bct('has been offered a placement on the waitlist at') }}
              </span>
              {{ task.provider_name }}
            </h3>
          </template>

          <template v-else-if="task.agreement">
            <h3 class="fs-18">
              <template v-if="taskTitle">
                <span class="fw-500">
                  {{ `${$bct(task.action)} ${$bct('for')}` }}
                </span>
                <span>{{ taskTitle }}</span>
              </template>

              <template v-else>
                <span class="fw-500">
                  {{ $bct(task.action) }}:
                  {{ $bct('An agreement is ready for review and requires your action') }}
                </span>
              </template>
            </h3>
          </template>

          <template v-else>
            <h3 class="fs-18">
              <span>{{ taskTitle }}</span>
            </h3>
          </template>
        </v-col>

        <!-- Show buttons for the available actions -->
        <v-col class="d-flex justify-end ga-3">
          <v-btn
            v-if="task.action === 'Sign agreement'"
            @click="openAgreement(task)"
            class="fs-16"
            color="primary"
          >
            {{ $bct(task.action) }}
          </v-btn>

          <v-btn
            v-else-if="task.schema_id"
            @click="openFormDialog()"
            class="fs-16"
            color="primary"
          >
            {{ $bct(task.action) }}
          </v-btn>

          <template v-else-if="task.wait_list_offer">
            <v-btn
              @click="deleteWaitlistRequest"
              class="fs-16"
              variant="outlined"
            >
              {{ $bct('Remove') }}
            </v-btn>

            <v-btn
              @click="openWaitListOfferReviewDialog()"
              class="fs-16"
              color="primary"
            >
              {{ $bct(task.action) }}
            </v-btn>
          </template>

          <v-btn
            v-else-if="task.agreement"
            @click="openAgreement(task)"
            class="fs-16"
            color="primary"
          >
            {{ $bct(task.action) }}
          </v-btn>

          <v-btn
            v-else
            :to="link"
            class="fs-16"
            color="primary"
            role="link"
          >
            {{ $bct(task.action) }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>

  <AgreementDialog
    @change="emit('reload-tasks')"
    ref="agreementDialog"
    :processing="processing"
    role="parent"
  />

  <WaitListOfferReviewDialog
    v-if="task.wait_list_offer"
    @close="$emit('reload-tasks')"
    ref="waitListOfferReviewDialog"
    :close-on-save="true"
  />

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useRouter } from 'vue-router';

import AgreementDialog from '@/shared/components/agreement/AgreementDialog.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import { getNextStepLink } from '@/parent/services/dashboard';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import WaitListOfferReviewDialog from '@/parent/components/wait-list-offers/WaitListOfferReviewDialog.vue';

const { api } = useApi();
const eventBus = useEventBus();
const router = useRouter();
const $bct = inject('translator');

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['reload-tasks']);

const agreementDialog = ref(null);
const confirmDialog = ref(null);
const processing = ref(false);
const waitListOfferReviewDialog = ref(null);

const isExpiredAgreement = computed(() => {
  return props.task?.agreement && props.task?.status === 'Expired';
});

const link = computed(() => getNextStepLink(props.task));

const taskTitle = computed(() => {
  let parts = [];

  if (props.task.child_name) {
    parts.push(`${props.task.child_name},`);
  }

  parts.push($bct(props.task.program_name));
  parts.push(props.task.provider_name);
  parts.push($bct(props.task.form_name));

  let title = parts
    .filter((part) => part?.trim() !== '')
    .join(' ')
    .trim();

  // Extra spacing is needed but it won't impact Translation if added before child name
  // This space exists in the browser but is not visible in the span
  if (props.task.child_name) {
    title = ' ' + title;
  }

  return title;
});

async function deleteWaitlistRequest() {
  const confirm = await confirmDialog.value.confirmWithText(
    'By clicking "I understand" I acknowledge my child will be taken off this provider\'s waitlist.',
    {
      title: 'Remove my child from the waitlist',
      confirmButtonText: 'I understand',
    },
  );
  if (!confirm) return;

  const response = await api.organization.waitlistOffer.destroy(props.task.id);
  if (response?.status !== 202) {
    return;
  }

  emit('reload-tasks');
  eventBus.chime('Removed');
}

function openAgreement(agreement) {
  agreementDialog.value.open(agreement.id);
}

async function openFormDialog() {
  await router.push({
    name: 'FormContinue',
    params: {
      formId: props.task.id,
      schemaId: props.task.schema_id,
    },
    query: {
      backTo: 'Dashboard',
    },
  });
}

function openWaitListOfferReviewDialog() {
  waitListOfferReviewDialog.value.open(props.task.id);
}
</script>
